import { useEffect, useState } from "react";

import { useResizeObserver } from "src/utils/useResizeObserver";

export const useIsContentOverflows = <T extends HTMLElement>(
  ref: React.RefObject<T | null>,
  options?: {
    enabled?: boolean;
  },
) => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const { enabled = true } = options ?? {};

  // A window resize is not the only thing that changes the element's box:
  // a side pane opening, fonts finishing loading, or sibling content
  // reflowing all change available width without firing a window resize.
  // Observe the element itself so the overflow state stays correct instead
  // of being stuck on the single mount measurement.
  useResizeObserver(enabled ? ref : null, () => {
    const el = ref.current;

    if (el) {
      setIsOverflowing(el.scrollWidth > el.clientWidth);
    }
  });

  useEffect(() => {
    if (!enabled) {
      setIsOverflowing(false);
      return;
    }

    const updateOverflowState = () => {
      const el = ref.current;

      if (el) {
        setIsOverflowing(el.scrollWidth > el.clientWidth);
      }
    };

    updateOverflowState();
    window.addEventListener("resize", updateOverflowState);

    return () => {
      window.removeEventListener("resize", updateOverflowState);
    };
  }, [ref, enabled]);

  return isOverflowing;
};
